import { useGetUserProfile } from "hooks/queries/use-get-user-profile.hook";
import type { ProfileData } from "types/types";

export type { ProfileData };

interface PhoneNumber {
  number?: string;
  description?: string;
  countryCode?: number;
}

interface Location {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postal?: string;
  isDefault?: boolean;
  type?: string;
}

interface CustomField {
  name?: string;
  value?: string;
}

interface UserProfileData {
  email?: string;
  phoneNumbers?: PhoneNumber[];
  customFields?: CustomField[];
  userProfile?: {
    firstName?: string;
    lastName?: string;
    preferredFirstName?: string;
    dateOfBirth?: string;
    sex?: string;
    genderIdentity?: string[];
    locations?: Location[];
    optedIntoMarketingCommunications?: boolean;
    notificationPreferences?: {
      disableSms?: boolean;
    };
  };
}

const parsePhoneNumbers = (phoneNumbers?: PhoneNumber[]) => {
  if (!phoneNumbers) return { primary: undefined, mobile: undefined };

  const primaryPhone = phoneNumbers.find((p) => p.description === "primary");
  const mobilePhone = phoneNumbers.find((p) => p.description === "mobile");

  return {
    primary: primaryPhone?.number,
    mobile: mobilePhone?.number,
  };
};

const parseMailingAddress = (locations?: Location[]) => {
  if (!locations || locations.length === 0) return undefined;

  const defaultLocation = locations.find((loc) => loc.isDefault);
  const location = defaultLocation || locations[0];

  return {
    streetAddress: location.address1,
    aptSuiteUnit: location.address2,
    city: location.city,
    state: location.state,
    zipCode: location.postal,
  };
};

const parseDisabledFields = (customFields?: CustomField[]) => {
  if (!customFields) {
    return {
      isFirstNameLocked: false,
      isLastNameLocked: false,
      isBirthDateLocked: false,
    };
  }
  // Find ALL disabledFields entries
  const disabledFieldEntries = customFields.filter(
    (f) => f.name === "disabledFields"
  );
  if (disabledFieldEntries.length === 0) {
    return {
      isFirstNameLocked: false,
      isLastNameLocked: false,
      isBirthDateLocked: false,
    };
  }
  // Flatten all disabled field names
  const disabledFieldNames = disabledFieldEntries.flatMap(
    (entry) => entry.value?.split(",").map((f) => f.trim()) || []
  );
  return {
    isFirstNameLocked: disabledFieldNames.includes("firstName"),
    isLastNameLocked: disabledFieldNames.includes("lastName"),
    isBirthDateLocked: disabledFieldNames.includes("dateOfBirth"),
  };
};

export const useProfileData = () => {
  const { data, isLoading, isError, error, refetch } = useGetUserProfile();

  const apiData = data as UserProfileData;
  const userProfile = apiData?.userProfile;

  const phoneNumbers = parsePhoneNumbers(apiData?.phoneNumbers);
  const mailingAddress = parseMailingAddress(userProfile?.locations);
  const disabledFields = parseDisabledFields(apiData?.customFields);

  const profileData: ProfileData | undefined = data
    ? {
        firstName: userProfile?.firstName,
        lastName: userProfile?.lastName,
        preferredName: userProfile?.preferredFirstName,
        dateOfBirth: userProfile?.dateOfBirth,
        selfIdentifiedGender: userProfile?.sex,
        email: apiData?.email,
        primaryPhoneNumber: phoneNumbers.primary,
        mobilePhoneNumber: phoneNumbers.mobile,
        mailingAddress,
        ...disabledFields,
        isAddressLocked: false,
        isContactInfoLocked: false,
        optedIntoMarketingCommunications:
          userProfile?.optedIntoMarketingCommunications,
        notificationPreferences: userProfile?.notificationPreferences,
      }
    : undefined;

  return {
    data: profileData,
    isLoading,
    isError,
    error,
    refetch,
  };
};
